import { NSARouterMakerResult } from "namirasoft-account-react";
import { JournalEntryMode, NamirasoftERPServer } from "namirasoft-erp";
import { NSMenuActionProps } from "namirasoft-site-react";
import { ErpRoutes } from "./ErpRoutes";

type JournalEntryMenuRow = { id: string; finalized: boolean; mode: JournalEntryMode };

const REQUEST_MODES: JournalEntryMode[] = [JournalEntryMode.RequestToAdd, JournalEntryMode.RequestToEdit, JournalEntryMode.RequestToDelete];

function isRequest(row: JournalEntryMenuRow): boolean
{
    return REQUEST_MODES.includes(row.mode);
}

function runForEach(getIDs: () => string[], call: (id: string) => Promise<unknown>, reload: () => void): NSMenuActionProps["onClick"]
{
    return {
        action: async (onFinished) =>
        {
            try
            {
                for (let id of getIDs())
                    await call(id);
                reload();
            }
            catch (err)
            {
                // The server already reported the error through the notifier.
            }
            finally
            {
                onFinished();
            }
        },
        showLoading: true
    };
}

export class JournalEntryMenus
{
    static setFinalized(server: NamirasoftERPServer, getIDs: () => string[], getRows: () => JournalEntryMenuRow[], reload: () => void): NSMenuActionProps
    {
        return {
            id: "je-set-finalized",
            menu_item: "Set Finalized",
            isActive: () =>
            {
                let rows = getRows();
                if (rows.length === 0)
                    return false;
                return rows.every(r => !r.finalized && !isRequest(r));
            },
            onClick: runForEach(getIDs, (id) => server.journal_entry.SetFinalized(id, { finalized: true }), reload)
        };
    }
    static unsetFinalized(server: NamirasoftERPServer, getIDs: () => string[], getRows: () => JournalEntryMenuRow[], reload: () => void): NSMenuActionProps
    {
        return {
            id: "je-unset-finalized",
            menu_item: "Unset Finalized",
            isActive: () =>
            {
                let rows = getRows();
                if (rows.length === 0)
                    return false;
                return rows.every(r => r.finalized);
            },
            onClick: runForEach(getIDs, (id) => server.journal_entry.SetFinalized(id, { finalized: false }), reload)
        };
    }
    /** Opens the request editor for one normal entry (creates a "RequestToEdit") or one pending add/edit request (updates it). */
    static requestEdit(props: NSARouterMakerResult, getIDs: () => string[], getRows: () => JournalEntryMenuRow[]): NSMenuActionProps
    {
        return {
            id: "je-request-edit",
            menu_item: "Request Edit",
            isActive: () =>
            {
                let rows = getRows();
                if (rows.length !== 1)
                    return false;
                return rows[0].mode !== JournalEntryMode.RequestToDelete;
            },
            onClick: {
                action: (onFinished) =>
                {
                    let ids = getIDs();
                    if (ids.length === 1)
                        props.url.navigate(ErpRoutes.JournalEntryRequestEdit(ids[0]));
                    onFinished();
                },
                showLoading: false
            }
        };
    }
    /** Creates a "RequestToDelete" for every selected normal entry. */
    static requestDelete(server: NamirasoftERPServer, getIDs: () => string[], getRows: () => JournalEntryMenuRow[], reload: () => void): NSMenuActionProps
    {
        return {
            id: "je-request-delete",
            menu_item: "Request Delete",
            isActive: () =>
            {
                let rows = getRows();
                if (rows.length === 0)
                    return false;
                return rows.every(r => r.mode === JournalEntryMode.Normal);
            },
            onClick: runForEach(getIDs, (id) => server.journal_entry_request.Delete(id), reload)
        };
    }
    /** Withdraws (deletes) the selected pending requests through the request endpoint. */
    static withdrawRequest(server: NamirasoftERPServer, getIDs: () => string[], getRows: () => JournalEntryMenuRow[], reload: () => void): NSMenuActionProps
    {
        return {
            id: "je-withdraw-request",
            menu_item: "Withdraw Request",
            isActive: () =>
            {
                let rows = getRows();
                if (rows.length === 0)
                    return false;
                return rows.every(isRequest);
            },
            onClick: runForEach(getIDs, (id) => server.journal_entry_request.Delete(id), reload)
        };
    }
    static approve(server: NamirasoftERPServer, getIDs: () => string[], getRows: () => JournalEntryMenuRow[], reload: () => void): NSMenuActionProps
    {
        return {
            id: "je-approve",
            menu_item: "Approve Request",
            isActive: () =>
            {
                let rows = getRows();
                if (rows.length === 0)
                    return false;
                return rows.every(isRequest);
            },
            onClick: runForEach(getIDs, (id) => server.journal_entry.Approve(id), reload)
        };
    }
    static reject(server: NamirasoftERPServer, getIDs: () => string[], getRows: () => JournalEntryMenuRow[], reload: () => void): NSMenuActionProps
    {
        return {
            id: "je-reject",
            menu_item: "Reject Request",
            isActive: () =>
            {
                let rows = getRows();
                if (rows.length === 0)
                    return false;
                return rows.every(isRequest);
            },
            onClick: runForEach(getIDs, (id) => server.journal_entry.Reject(id), reload)
        };
    }
}